/**
 * Risk CSV / JSON import + export helpers.
 *
 * Mirror of step5-attack-paths/attackPathCsv.ts. Export uses the
 * read-mode column labels; the `damage_ref` column resolves to
 * `damage_scenario_id` (newer) or `damage_id` (older) for display.
 * On import the same field is normalised back to `damage_id` so
 * the record always lands with a single, well-known key.
 */
import {
  RISK_COLUMNS_READ,
  type RiskRecord,
} from './riskFieldSchema';

const EXPORT_COLUMNS = RISK_COLUMNS_READ;

const escapeCsv = (raw: unknown): string => {
  if (raw == null) return '';
  if (Array.isArray(raw)) return `"${raw.join('; ').replace(/"/g, '""')}"`;
  const s = String(raw);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/** Read a value for export. The synthetic `damage_ref` column has
 *  no underlying field — fall back to scenario_id / damage_id. */
function readCell(row: RiskRecord, key: string): unknown {
  if (key === 'damage_ref') return row.damage_scenario_id ?? row.damage_id ?? '';
  return (row as Record<string, unknown>)[key];
}

/** Trigger a CSV download with `risks_YYYY-MM-DD.csv` filename. */
export function exportRisksToCsv(risks: RiskRecord[]): void {
  const header = EXPORT_COLUMNS.map((c) => c.label).join(',');
  const rows = risks.map((r) =>
    EXPORT_COLUMNS.map((c) => escapeCsv(readCell(r, c.key))).join(','),
  );
  const csv = [header, ...rows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `risks_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
}

function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.length > 0));
}

const HEADER_OVERRIDES: Record<string, string> = {
  id: 'risk_id',
  asset: 'asset_id',
  threat: 'threat_id',
  damage: 'damage_id',
  attack_path: 'attack_path_id',
  impact: 'impact_rating',
  feasibility: 'attack_feasibility_rating',
  value: 'risk_value',
  level: 'risk_level',
  treatment: 'treatment_decision',
  priority: 'treatment_priority',
  justification: 'treatment_justification',
};

function headerToKey(header: string): string {
  const col = EXPORT_COLUMNS.find(
    (c) => c.label.toLowerCase() === header.trim().toLowerCase(),
  );
  if (col) {
    // `damage_ref` is synthetic; map back to canonical `damage_id`.
    return col.key === 'damage_ref' ? 'damage_id' : col.key;
  }
  const slug = header.trim().toLowerCase().replace(/\s+/g, '_');
  return HEADER_OVERRIDES[slug] ?? slug;
}

const NUMERIC_KEYS = new Set(['risk_value']);

/** Parse a CSV file into risk records. Numeric coercion for risk_value. */
export function parseRiskCsv(text: string): RiskRecord[] {
  const rows = parseCsvText(text);
  if (rows.length === 0) return [];
  const headers = rows[0].map(headerToKey);
  return rows.slice(1).map((row) => {
    const out: Record<string, unknown> = {};
    headers.forEach((key, i) => {
      const raw = row[i] ?? '';
      if (NUMERIC_KEYS.has(key)) {
        const n = Number(raw);
        out[key] = Number.isFinite(n) ? n : raw;
      } else {
        out[key] = raw;
      }
    });
    return out as RiskRecord;
  });
}

/** Parse a JSON file. Accepts `[…]`, `{ risk_assessments: [...] }`,
 *  legacy `{ risks: [...] }` or `{ risk_assessment: [...] }`. */
export function parseRiskJson(text: string): RiskRecord[] {
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed as RiskRecord[];
  if (parsed && Array.isArray(parsed.risk_assessments)) return parsed.risk_assessments as RiskRecord[];
  if (parsed && Array.isArray(parsed.risk_assessment)) return parsed.risk_assessment as RiskRecord[];
  if (parsed && Array.isArray(parsed.risks)) return parsed.risks as RiskRecord[];
  throw new Error('JSON must be an array of risks or `{ risk_assessments: [...] }`');
}

export async function parseRiskFile(file: File): Promise<RiskRecord[]> {
  const text = await file.text();
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return parseRiskJson(text);
  if (ext === 'csv') return parseRiskCsv(text);
  try {
    return parseRiskJson(text);
  } catch {
    return parseRiskCsv(text);
  }
}
